import { z } from 'zod';
import {
  iconnectSyncConfigSchema,
  workflowIntroConfigSchema,
  type WorkflowIntroConfig,
  type WorkflowMetadata,
} from './form-workflows.js';

// Settings that describe how one form behaves wherever it is used. They started on the workflow (intro config,
// messages, metadata) and move onto the form, so a form behaves the same in every workflow that uses it.
// Stored as JSON on the form and parsed on read, so a form with nothing stored gets the defaults below.

/** How a person starts filling in the form. */
export const formIntakeSchema = z.object({
  warningMessage: z.string().prefault(''),
  showSpeechOption: z.boolean().prefault(false),
  showManualButton: z.boolean().prefault(true),
  showFileUploadOption: z.boolean().prefault(false),
  showCancelButton: z.boolean().prefault(true),
  extractionPrompt: z.string().optional(),
  disclaimerEnabled: z.boolean().prefault(false),
  disclaimerTitle: z.string().optional(),
  disclaimerText: z.string().optional(),
  introResponse: workflowIntroConfigSchema.shape.introResponse,
  dashboardElement: workflowIntroConfigSchema.shape.dashboardElement,
});

/** Where a submitted form is sent. */
export const formDeliverySchema = z.object({
  sendToEndpoint: z.boolean().prefault(false),
  dataConnectionIdentifier: z.string().optional(),
  endpoint: z.string().optional(),
});

/**
 * What happens when the form is submitted. Kept as one object so that, if submission later becomes its own step
 * over several forms, it can be lifted out as it is.
 */
export const formSubmissionSchema = z.object({
  submitButtonText: z.string().optional(),
  successMessage: z.string().prefault(''),
  responseMessage: z.string().prefault(''),
  delivery: formDeliverySchema.prefault({}),
  iconnectSync: iconnectSyncConfigSchema.optional(),
});

/** What is stored: any field may be missing. Parse with the schema to get the defaults filled in. */
export type FormIntakeInput = z.input<typeof formIntakeSchema>;
export type FormSubmissionInput = z.input<typeof formSubmissionSchema>;

export type FormIntake = z.infer<typeof formIntakeSchema>;
export type FormDelivery = z.infer<typeof formDeliverySchema>;
export type FormSubmission = z.infer<typeof formSubmissionSchema>;

/** The workflow settings that describe how a form behaves, which are what moves onto the form. */
export type WorkflowFormSettings = {
  introConfig: WorkflowIntroConfig;
  successMessage: string;
  responseMessage: string;
  metadata?: WorkflowMetadata;
};

/**
 * What to store on a form so it behaves as the workflow did. The intro config splits in two: how the form is started
 * stays intake, and its submit button text and iConnect sync go with the submission. A workflow's follow-ups and
 * response type describe the workflow, not a form, and stay on it.
 */
export function formSettingsFromWorkflow(workflow: WorkflowFormSettings): {
  intake: FormIntakeInput;
  submission: FormSubmissionInput;
} {
  const { submitButtonText, iconnectSync, ...intake } = workflow.introConfig;
  const { sendToEndpoint, dataConnectionIdentifier, endpoint } = workflow.metadata ?? {};
  return {
    intake,
    submission: {
      submitButtonText,
      successMessage: workflow.successMessage,
      responseMessage: workflow.responseMessage,
      delivery: { sendToEndpoint: sendToEndpoint ?? false, dataConnectionIdentifier, endpoint },
      iconnectSync,
    },
  };
}

/** The intro config the chat client reads, put back together from a form's intake and submission. */
export function introConfigFromFormSettings(intake: FormIntake, submission: FormSubmission): WorkflowIntroConfig {
  return {
    ...intake,
    submitButtonText: submission.submitButtonText,
    iconnectSync: submission.iconnectSync,
  };
}
